/**
 * React Query mutation that chains
 *   PUT /api/persona-bundles/:id        (mints new inactive version)
 *   POST /api/persona-bundles/:newId/activate  (flips is_current)
 * for the Workshop's Save flow.
 *
 * The two-step shape is dictated by the versioned bundle API (P1.T1):
 * PUT creates a NEW row, activate is a separate transaction. We expose
 * a single mutation to the caller; the caller (composerStore.commitDraft
 * via the registered mutation) doesn't need to know.
 *
 * `UpdatePersonaBundleArgs.updates` carries all 8 `PersonaBundleUpdate`
 * content fields (`blocks`, `default_skills`, `auto_skills`,
 * `tool_bindings`, `workflow_bindings`, `channel_overrides`, `policy`,
 * `variable_values`), each optional — the version-history Restore path
 * (persona-version-history design §5.4) needs all 8 for full-fidelity
 * restore, widening past the Composer's original blocks-only payload.
 * The PUT body's `PersonaBundleUpdate` schema accepts any subset; unset
 * fields are preserved from the prior version (P1.T1 service contract).
 *
 * On success, invalidates the persona-bundle query for the persona
 * identified by the ACTIVATED bundle's `persona_id`. We deliberately
 * source personaId from the bundle response rather than closing over
 * a hook parameter — eliminates the stale-closure failure mode where
 * the URL personaId could change between mutate() and onSuccess.
 *
 * Failure modes:
 *   - PUT fails: mutation rejects with PUT error; activate never fires.
 *   - PUT succeeds but activate fails: mutation rejects with activate
 *     error; the new bundle row is an orphan (queryable via list
 *     endpoint for ops triage). Retry creates yet another row — v1
 *     accepts this; no retry-on-activate logic ships in T2 ("no
 *     real users until phases ship" — operator framing 2026-05-21).
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type { PersonaBundleBlock, PersonaBundleResponse } from '@/types/personaWizard'
import { personaBundleQueryKey } from './usePersonaBundle'

export interface UpdatePersonaBundleArgs {
  bundleId: string
  // All 8 content fields, all optional. The Composer's Save flow only
  // ever sends the 5 Workshop-managed fields (blocks, default_skills,
  // auto_skills, tool_bindings, workflow_bindings); the version-history
  // Restore handler sends all 8 for full-fidelity restore. PersonaBundleUpdate
  // accepts any subset; unset fields are preserved from the prior version
  // (P1.T1 service contract).
  updates: {
    blocks?: PersonaBundleBlock[]
    default_skills?: { name: string }[]
    auto_skills?: { name: string }[]
    tool_bindings?: Record<string, unknown>
    workflow_bindings?: unknown[]
    channel_overrides?: Record<string, unknown>
    policy?: Record<string, unknown>
    variable_values?: Record<string, string>
  }
}

export function useUpdatePersonaBundle(): UseMutationResult<
  PersonaBundleResponse,
  Error,
  UpdatePersonaBundleArgs
> {
  const qc = useQueryClient()
  return useMutation<PersonaBundleResponse, Error, UpdatePersonaBundleArgs>({
    mutationFn: async ({ bundleId, updates }) => {
      const newBundle = await apiClient.put<PersonaBundleResponse>(
        `/api/persona-bundles/${bundleId}`,
        updates,
      )
      const activated = await apiClient.post<PersonaBundleResponse>(
        `/api/persona-bundles/${newBundle.id}/activate`,
        {},
      )
      return activated
    },
    onSuccess: (activated) => {
      // Use the activated bundle's persona_id (the bundle row's
      // canonical owner) — not a hook arg — so mid-mutation route
      // changes can't mis-target the invalidation.
      qc.invalidateQueries({
        queryKey: personaBundleQueryKey(activated.persona_id),
      })
    },
  })
}
